import { Mutation } from '@apollo/client/react/components';
import { useQuery } from '@apollo/client/react/hooks';
import { CKButton } from '@clearkit/react';
import { Link } from 'react-router-dom';

import ConfirmDeleteModal from '~/components/ConfirmDeleteModal';
import Footer from '~/components/Footer';
import ToastContainer from '~/containers/Toasts';
import history from '~/lib/history';
import paths from '~/lib/paths';
import { trackDestinationDeleted } from '~/lib/segmentAnalytics';
import { legacySegmentType } from '~/lib/segments';

import FormWrapper from '../components/FormWrapper';
import DestinationFormFactory from '../destinationConfigs/DestinationFormFactory';
import destroyMutation from './destroy.graphql';
import query from './query.graphql';
import updateMutation from './update.graphql';

export default function SegmentDestinationEditPage({
  destinationId,
  segmentId,
  segmentType,
}) {
  const [errors, setErrors] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDestroyComplete = (destination, segment) => {
    const path = paths.segmentDestinations(segment.type, segment.id);

    trackDestinationDeleted(destination, segment);

    ToastContainer.addSuccess('Destination removed');

    history.push(path);
  };

  const handleUpdate = (updateDestination, destination, destinationConfig) => {
    updateDestination({
      variables: {
        input: {
          id: destination.id,
          ...destinationConfig,
        },
      },
    });
  };

  const handleUpdateComplete = ({ response: { node, errors } }) => {
    if (errors.length) {
      ToastContainer.addError('There was a problem saving this destination.');
      setErrors(errors);
      return;
    }

    ToastContainer.addSuccess('Destination saved');

    const { segment } = node;

    const path = paths.segmentDestinations(segment.type, segment.id);
    history.push(path);
  };

  const attributeType = legacySegmentType(segmentType);

  const variables = {
    attributeType,
    destinationId,
    segmentId,
    segmentType,
  };

  const { data = {}, refetch, loading, error } = useQuery(query, {
    variables,
    skip: !destinationId,
  });

  const { segment = {} } = data;
  const { destination = {} } = segment;

  const accountConnectors = {
    facebook: data.facebookAccountConnector,
    google: data.googleAccountConnector,
    hubspot: data.hubspotAccountConnector,
    salesforce: data.salesforceAccountConnector,
    slack: data.slackAccountConnector,
  };

  const FormFooter = ({ destinationConfig, submitDisabled }) => {
    return (
      <Footer
        left={
          <CKButton
            disabled={loading}
            onClick={() => {
              setShowDeleteModal(true);
            }}
            variant="simple"
            variantColor="red">
            Remove
          </CKButton>
        }
        right={
          <>
            <CKButton
              as={Link}
              to={paths.segmentDestinations(segment.type, segment.id)}
              variant="simple"
              variantColor="default">
              Cancel
            </CKButton>
            <Mutation
              mutation={updateMutation}
              onCompleted={handleUpdateComplete}
              refetchQueries={['SegmentDestinationsPage']}>
              {(updateDestination, { loading }) => (
                <CKButton
                  disabled={loading || submitDisabled}
                  onClick={() => {
                    handleUpdate(
                      updateDestination,
                      destination,
                      destinationConfig,
                    );
                  }}
                  type="button"
                  variantColor="green"
                  variant="bold">
                  Save changes
                </CKButton>
              )}
            </Mutation>
          </>
        }
      />
    );
  };

  return (
    <FormWrapper
      loading={loading || !destinationId}
      refetch={refetch}
      error={error}>
      {() => (
        <>
          <DestinationFormFactory
            errors={errors}
            accountConnectors={accountConnectors}
            attributes={data.attributes}
            refetch={refetch}
            segment={segment}
            destination={destination}
            destinationOptions={data.destinationOptions}
            FormFooter={FormFooter}
            type={destination.type}
          />
          <Mutation
            mutation={destroyMutation}
            onCompleted={() => {
              handleDestroyComplete(destination, segment);
            }}
            variables={{
              input: {
                id: destination.id,
              },
            }}
            refetchQueries={['SegmentDestinationsPage']}>
            {(mutation) => (
              <ConfirmDeleteModal
                open={showDeleteModal}
                canDelete
                type="destination"
                item={destination}
                deleteItem={mutation}
                onCompleted={() => setShowDeleteModal(false)}
                closeModal={() => {
                  setShowDeleteModal(false);
                }}
              />
            )}
          </Mutation>
        </>
      )}
    </FormWrapper>
  );
}

SegmentDestinationEditPage.propTypes = {
  destinationId: PropTypes.string,
};
